/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    CATALOGUE_MODE,
    DEFAULT_ALTERNATIVE_ORDER,
    GODIA_ORDER_INSERTION_ERROR,
    MAX_RETRY_ATTAINT,
    Retrait_C_C,
    TCPOS,
    WAITING_TIME_BETWEEN_RETRY,
} from "@constants";
import { useMutation } from "@tanstack/react-query";
import { useSnapshot } from "valtio";

import React from "react";
import { useSelector } from "react-redux";

import { store } from "@store";

import {
    handleOnErrorPutCommand,
    handlePrintClientTicket,
    handlePrintVoucher,
    handlePutClickAndCollectOrder,
    handlePutCommand,
    putOrderSuccessfullyInserted,
} from "@helpers/general";
import { updatePaymentModes } from "@helpers/payment";

import LoadingScreen from "@components/common/LoadingScreen";
import { LogoWrapper } from "@components/common/LogoWrapper";
import { PreviewBackgroundWrapper } from "@components/common/PreviewBackgroundWrapper";
import { validateTCPOSCommand } from "@components/OrderTaking/Helpers";

import { EmailSendedWithSuccessModalWrapper } from "@pages/Kiosk/EmailSendedWithSuccessModalWrapper";
import { Container } from "@pages/Kiosk/Steps/FinalMessageStep/Container";
import { ContainerContent } from "@pages/Kiosk/Steps/FinalMessageStep/ContainerContent";
import { OrderNumberWrapper } from "@pages/Kiosk/Steps/FinalMessageStep/OrderNumberWrapper";
import {
    kioskStore,
    setCommandInsertionProblemModalOpened,
    setIsOrderInsertedSuccessfully,
    setIsOrderInsertionRefused,
    setOrderNumber,
} from "@pages/Kiosk/store";

type ClickAndCollectInput = {
    clickAndCollectOrderId: string;
    localPaymentModes: PaymentModesType;
};

export const FinalMessageStep = React.memo((): JSX.Element => {
    const { posEditor } = useSnapshot(store);
    const {
        selectedRegulationModeValue,
        selectedSaleModeValue,
        isCreatingVoucherAccepted,
        paymentModes,
        isSendedEmailWithSuccessModalOpened,
        isSendMailWithPdfActive,
        clickAndCollectOrderId,
    } = useSnapshot(kioskStore);

    const middleWareOrder = useSelector(
        (state: any) => state.middlwareOrderSlice.middleWareOrder
    );

    const localPaymentModes = updatePaymentModes(
        paymentModes,
        isCreatingVoucherAccepted
    );

    const { mutateAsync: putCommand, isLoading: isLoadingPutCommand } =
        useMutation({
            mutationFn: handlePutCommand,
            cacheTime: 0,
            retry: MAX_RETRY_ATTAINT,

            retryDelay: WAITING_TIME_BETWEEN_RETRY,
            onSuccess: (data: any) => {
                if (data.code === 2) {
                    console.log("command inserted with success");
                    putOrderSuccessfullyInserted(data);
                    return;
                } else {
                    console.log(
                        `error while inserting command with retryCounter with invalid response ${data}`
                    );
                    const isGodiaError =
                        data?.code === GODIA_ORDER_INSERTION_ERROR;
                    handleOnErrorPutCommand(middleWareOrder, isGodiaError);
                }
            },
            onError: (error: any) => {
                console.error("Error in putCommand:", error);
                handleOnErrorPutCommand(middleWareOrder);
            },
        });

    const {
        mutateAsync: putCommandClickAndCollect,
        isLoading: isLoadingClickAndCollect,
    } = useMutation<any, Error, ClickAndCollectInput>({
        mutationFn: handlePutClickAndCollectOrder,
        retry: MAX_RETRY_ATTAINT,
        cacheTime: 0,
        retryDelay: WAITING_TIME_BETWEEN_RETRY,
        onSuccess: (data: any) => {
            console.log({ data });
            if (data?.code === 2) {
                console.log("The order inserted successfully");
                const orderNumber = String(data.response[0]).slice(
                    8,
                    data.response[0].length
                );

                const isAlternativeOrderNumber =
                    data.alternative &&
                    data.alternative !== DEFAULT_ALTERNATIVE_ORDER;

                setOrderNumber(
                    isAlternativeOrderNumber ? data.alternative : orderNumber
                );

                console.log("Voucher accepted?", { isCreatingVoucherAccepted });

                if (isCreatingVoucherAccepted) {
                    console.log("Creating voucher is accepted, printing...");
                    handlePrintVoucher(data.response[1]);
                }

                handlePrintClientTicket(data.response[0]);
                setIsOrderInsertedSuccessfully(true);
            } else {
                console.log("Error while inserting the order.");
                console.log("Refunding deposited amount...");
                console.log(
                    "Payment modes:",
                    JSON.stringify(localPaymentModes)
                );
                if (data?.code === GODIA_ORDER_INSERTION_ERROR) {
                    setCommandInsertionProblemModalOpened({
                        isInsertError: false,
                        isGodiaError: true,
                    });
                } else {
                    setCommandInsertionProblemModalOpened({
                        isInsertError: true,
                        isGodiaError: false,
                    });
                }

                setIsOrderInsertionRefused(true);
            }
        },
        onError: (error: any) => {
            console.error("Error in putCommandClickAndCollect:", error);
            setCommandInsertionProblemModalOpened({
                isInsertError: true,
                isGodiaError: false,
            });
            setIsOrderInsertionRefused(true);
        },
    });

    React.useEffect(() => {
        console.log({ selectedSaleModeValue });
        if (
            !(TCPOS === posEditor) &&
            process.env.REACT_APP_MODE !== CATALOGUE_MODE
        ) {
            if (
                Object.keys(middleWareOrder).length > 0 &&
                selectedSaleModeValue !== Retrait_C_C
            ) {
                console.log("handle put command V2");
                putCommand(middleWareOrder);
            }
            if (selectedSaleModeValue === Retrait_C_C) {
                console.log("handle put command for click and collect V2");
                putCommandClickAndCollect({
                    clickAndCollectOrderId,
                    localPaymentModes,
                });
            }
        }
        if (
            TCPOS === posEditor &&
            selectedRegulationModeValue === "atTheCounter"
        ) {
            console.log("handle validate command for TCPOS");
            validateTCPOSCommand();
        }
    }, [middleWareOrder]);

    return (
        <PreviewBackgroundWrapper>
            {isLoadingPutCommand || isLoadingClickAndCollect ? (
                <>
                    <LogoWrapper />
                    <LoadingScreen />
                </>
            ) : (
                <Container>
                    {isSendMailWithPdfActive === true &&
                    isSendedEmailWithSuccessModalOpened ? (
                        <EmailSendedWithSuccessModalWrapper />
                    ) : null}
                    <LogoWrapper />
                    <ContainerContent />
                    <OrderNumberWrapper />
                </Container>
            )}
        </PreviewBackgroundWrapper>
    );
});

FinalMessageStep.displayName = "FinalMessageStep";
